import { h } from '@/lib/dom';
import { Skeleton, SkeletonList } from '@/components/Skeleton';
import { Table } from '@/components/Table';
import { SyncBadge } from '@/components/SyncBadge';
import { WindowSwitcher } from '@/components/WindowSwitcher';
import { chartsSection as sharedChartsSection } from '@/components/ChartsSection';
import type { ChartSpec } from '@/components/ChartsSection';
import { TopTile } from '@/components/TopTile';
import { DeepDiveTile } from '@/components/DeepDiveTile';
import { data } from '@/lib/data';
import { formatDate, formatNumber, parseDate, sliceRecent, WINDOW_LABELS, windowDays } from '@/lib/format';
import type { WindowKey } from '@/lib/format';
import { sportEmoji, sportLabel } from '@/lib/sport';
import { currentSession } from '@/lib/session';
import type { RouteContext } from '@/router';
import { navigate } from '@/router';
import { errorRetry } from '@/lib/states';
import type {
  IntegrationSummary,
  WhoopBySport,
  WhoopDetailPayload,
  WhoopPreviousMonth,
  WhoopRecoveryPoint,
  WhoopStrainPoint,
  WhoopWindowStats,
  WhoopWorkout,
} from '@/api/types';
import { pageLayout } from './layout';

const WINDOWS: WindowKey[] = ['7d', '30d', '90d'];

const SPORT_COLORS = ['#1564C0', '#A15C07', '#6B21A8', '#0A6E50', '#7A8594'];

const STRAIN_TOOLTIP =
  'Strain — нагрузка дня по шкале 0–21. ' +
  'До 10 — лёгкий день, 10–14 — умеренный, 14–18 — тяжёлый, 18+ — пиковый. ' +
  'Здесь среднее по дням, в которые была хотя бы одна тренировка: ' +
  'выходные и дни без тренировок не размывают значение.';

const DETAIL_TOOLTIPS: Record<string, string> = {
  'Энергия': 'Сколько ккал ушло на тренировки за период. Оценка по пульсу и длительности.',
  'Тренировочных дней': 'Сколько дней за период была хотя бы одна тренировка. Отвечает на вопрос «как часто», независимо от количества тренировок в день.',
  'Средняя длительность': 'Средняя длительность одной тренировки. Полное время от старта до финиша.',
  'Средний пульс': 'Средний пульс во время тренировок (не покоя). Косвенно показывает интенсивность.',
  'Среднее восстановление': 'WHOOP Recovery 0–100 — насколько организм восстановился к утру: зелёное >66, жёлтое 34–66, красное <34. Здесь — среднее за период.',
};

export async function renderTrainingPage(_ctx: RouteContext): Promise<Node> {
  const user = currentSession();
  if (!user) {
    navigate('/login', { replace: true });
    return h('div');
  }

  let currentWindow: WindowKey = '30d';
  let realOnly = true;
  let detail: IntegrationSummary<WhoopDetailPayload> | null = null;

  const content = h('div', { id: 'training-content', class: 'sleep-page' }, [
    h('div', { class: 'flex flex-col gap-6' }, [
      Skeleton({ variant: 'rect', height: '72px' }),
      SkeletonList(3),
      Skeleton({ variant: 'chart', height: '220px' }),
    ]),
  ]);

  const root = pageLayout({
    user,
    active: 'training',
    children: [content],
  });

  const onWindowChange = (w: WindowKey) => { currentWindow = w; render(); };
  const onRealOnlyChange = (v: boolean) => { realOnly = v; render(); };

  const render = () => {
    if (!detail) return;
    content.innerHTML = '';
    content.append(...buildSections(detail, currentWindow, realOnly, onWindowChange, onRealOnlyChange));
  };

  void (async () => {
    try {
      detail = await data.getWhoopDetail();
      render();
      document.dispatchEvent(new Event('route:settled'));
    } catch (err) {
      content.innerHTML = '';
      content.appendChild(errorRetry(err, 'Не удалось загрузить данные тренировок.', '/training'));
    }
  })();

  return root;
}

function buildSections(
  detail: IntegrationSummary<WhoopDetailPayload>,
  window: WindowKey,
  realOnly: boolean,
  onWindowChange: (w: WindowKey) => void,
  onRealOnlyChange: (v: boolean) => void,
): Node[] {
  const payload = detail.payload;
  const header = trainingHeader(detail, window, realOnly, onWindowChange, onRealOnlyChange);

  if (payload.connected === false || payload.error === 'not_connected' || payload.error === 'oauth_not_configured') {
    return [header, notConnectedSection(payload.message)];
  }
  if (payload.error) {
    return [header, errorSection(payload.error)];
  }
  if (!payload.windows) {
    return [header, notConnectedSection(payload.message)];
  }

  // «Только полноценные»: backend recomputes the workout-derived sections and
  // ships them under `real`. Recovery/strain series are day-level (physiology),
  // so they always come from the top-level payload regardless of the filter.
  const src = realOnly && payload.real?.windows ? payload.real : payload;

  const stats = src.windows?.[window] ?? payload.windows[window];
  const bySportByWindow = src.by_sport ?? null;
  const bySport = bySportByWindow ? bySportByWindow[window] ?? [] : [];
  const recentWorkouts = src.workouts_recent ?? [];
  const recovery = sliceRecent(payload.recovery_recent ?? [], window);
  const strain = sliceRecent(payload.strain_recent ?? [], window);

  return [
    header,
    topStatsRow(stats, payload.windows[window], payload.previous_month, window),
    detailedGrid(stats, window),
    bySportSection(bySport, window),
    chartsSection(recovery, strain, window),
    recentWorkoutsSection(recentWorkouts),
  ];
}

function trainingHeader(
  detail: IntegrationSummary<WhoopDetailPayload>,
  window: WindowKey,
  realOnly: boolean,
  onWindowChange: (w: WindowKey) => void,
  onRealOnlyChange: (v: boolean) => void,
): HTMLElement {
  return h('header', { class: 'sleep-header' }, [
    h('div', { class: 'sleep-titles' }, [
      h('h1', { class: 'sleep-title' }, 'Тренировки · WHOOP'),
      h('p', { class: 'sleep-meta' },
        `Окно до 90 дней · ${detail.window_start ?? ''}–${detail.window_end ?? ''}`),
    ]),
    h('div', { class: 'sleep-header-controls' }, [
      filterToggle(realOnly, onRealOnlyChange),
      h('div', { class: 'sleep-window-switcher' },
        WindowSwitcher({
          current: window,
          windows: WINDOWS,
          labelFor: (w) => WINDOW_LABELS[w],
          onChange: onWindowChange,
        })),
      h('div', { class: 'sleep-sync' }, SyncBadge(detail)),
    ]),
  ]);
}

// «Только полноценные тренировки» — переключатель, скрывающий пассивную/досуговую
// активность (прогулки, сауна, рыбалка, пейнтбол, несортированное «activity»).
function filterToggle(realOnly: boolean, onChange: (v: boolean) => void): HTMLElement {
  return h('label', {
    class: 'training-filter',
    title: 'Считать только силовые, кардио и полноценные сессии. '
      + 'Скрывает прогулки с низким strain, сауну, рыбалку, пейнтбол и несортированную активность.',
  }, [
    h('input', {
      class: 'training-filter-input',
      type: 'checkbox',
      checked: realOnly,
      aria: { label: 'Только полноценные тренировки' },
      on: { change: (ev: Event) => onChange((ev.target as HTMLInputElement).checked) },
    }),
    h('span', { class: 'training-filter-switch', 'aria-hidden': 'true' }),
    h('span', { class: 'training-filter-text' }, 'Только полноценные'),
  ]);
}

function notConnectedSection(message: string | null | undefined): HTMLElement {
  return h('section', { class: 'sleep-section' }, [
    h('h2', { class: 'sleep-section-header' }, [h('span', null, 'WHOOP не подключён')]),
    h('p', null, message ?? 'Чтобы появились данные тренировок — подключите WHOOP в настройках.'),
    h('a', { href: '/settings', class: 'btn btn-primary btn-sm mt-2' }, 'Перейти в настройки'),
  ]);
}

function errorSection(code: string): HTMLElement {
  const label = code === 'refresh_failed'
    ? 'Токен WHOOP отозван — переподключите интеграцию в настройках.'
    : code === 'network_error'
      ? 'Не удалось связаться с WHOOP API.'
      : `Ошибка WHOOP (${code}).`;
  return h('section', { class: 'sleep-section' }, [
    h('h2', { class: 'sleep-section-header' }, [h('span', null, 'Ошибка загрузки')]),
    h('p', null, label),
  ]);
}

function topStatsRow(
  stats: WhoopWindowStats,
  cardioStats: WhoopWindowStats,
  previousMonth: WhoopPreviousMonth | null | undefined,
  window: WindowKey,
): HTMLElement {
  const perWeekSub = stats.workouts_per_week != null
    ? `~ ${formatNumber(stats.workouts_per_week, 1)} в неделю`
    : undefined;
  const zone2 = zone2Tile(cardioStats, window);
  const monthLabel = previousMonthLabel(previousMonth?.month);
  const tiles = [
    TopTile({
      label: 'Тренировок за период',
      value: String(stats.total_workouts),
      sub: perWeekSub,
      tooltip: 'Сколько тренировок попало в выбранное окно. В один день может быть несколько тренировок. Незавершённые (in-progress) не считаются.',
    }),
    TopTile({
      label: 'Суммарное время',
      value: stats.total_duration_min > 0 ? `${formatNumber(stats.total_duration_min / 60, 1)} ч` : '—',
      tooltip: 'Сколько часов в сумме длились тренировки за период. Это полное время от старта до финиша, а не только время активной нагрузки.',
    }),
    TopTile({
      label: 'Strain',
      value: stats.avg_strain != null ? formatNumber(stats.avg_strain, 1) : '—',
      tooltip: STRAIN_TOOLTIP,
    }),
    TopTile(zone2),
    TopTile({
      label: 'Силовые',
      value: previousMonth ? String(previousMonth.strength_workouts) : '—',
      sub: monthLabel ? `${monthLabel} · прошлый месяц` : 'Нет данных',
      tooltip: 'Количество полноценных силовых тренировок за предыдущий календарный месяц. '
        + 'Функциональные тренировки и футбол не учитываются.',
    }),
    TopTile({
      label: 'Кардио',
      value: previousMonth ? String(previousMonth.cardio_workouts) : '—',
      sub: monthLabel ? `${monthLabel} · прошлый месяц` : 'Нет данных',
      tooltip: 'Количество полноценных кардио-тренировок за предыдущий календарный месяц: эллипс, велотренажёр или велосипед, '
        + 'бег, хайкинг и ходьба со Strain от 6. Функциональные тренировки и футбол не учитываются.',
    }),
  ];
  return h('div', { class: 'flex flex-col gap-3' }, [
    h('div', { class: 'sleep-top-grid training-top-grid' }, tiles),
    h('p', { class: 'sleep-section-hint' },
      'Z2, силовые и кардио считаются только по полноценным тренировкам и не зависят от переключателя; '
        + 'силовые и кардио — за прошлый календарный месяц.'),
  ]);
}

function zone2Tile(stats: WhoopWindowStats, window: WindowKey): Parameters<typeof TopTile>[0] {
  const workouts = stats.cardio_workouts;
  const withZone2 = stats.cardio_zone2_workouts;
  const minutes = stats.cardio_zone2_min;
  const windowLabel = WINDOW_LABELS[window];
  const tooltip = 'Время в зоне 2 по данным WHOOP. Учитываются только полноценные кардио: эллипс, велотренажёр или велосипед, '
    + 'бег, хайкинг и ходьба со Strain от 6. Функциональные тренировки и футбол не учитываются. '
    + 'Если WHOOP не отдал Z2 для части тренировок, показанная сумма неполная.';

  if (typeof workouts !== 'number' || typeof withZone2 !== 'number') {
    return { label: 'Z2 в кардио', value: '—', sub: 'Нет данных', tooltip };
  }
  if (workouts === 0) {
    return { label: 'Z2 в кардио', value: '0 мин', sub: `${windowLabel} · нет кардио`, tooltip };
  }
  if (withZone2 === 0) {
    return { label: 'Z2 в кардио', value: '—', sub: `${windowLabel} · нет данных Z2: 0 из ${workouts}`, tooltip };
  }
  if (minutes == null) {
    return { label: 'Z2 в кардио', value: '—', sub: `${windowLabel} · нет данных Z2: ${withZone2} из ${workouts}`, tooltip };
  }
  if (withZone2 < workouts) {
    return {
      label: 'Z2 в кардио',
      value: `${formatNumber(minutes, 1)} мин`,
      sub: `${windowLabel} · Неполные данные Z2: ${withZone2} из ${workouts}`,
      tooltip,
    };
  }
  return {
    label: 'Z2 в кардио',
    value: `${formatNumber(minutes, 1)} мин`,
    sub: `${windowLabel} · Z2: ${withZone2} из ${workouts}`,
    tooltip,
  };
}

function previousMonthLabel(month: string | null | undefined): string | null {
  const match = /^(\d{4})-(0[1-9]|1[0-2])$/.exec(month ?? '');
  if (!match) return null;
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, 1));
  const formatter = new Intl.DateTimeFormat('ru-RU', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
  return formatter.formatToParts(date)
    .filter((part) => part.type === 'month' || part.type === 'year')
    .map((part) => part.value)
    .join(' ');
}

function detailedGrid(stats: WhoopWindowStats, window: WindowKey): HTMLElement {
  const totalKcal = stats.total_kilojoule ? stats.total_kilojoule / 4.184 : null;
  const avgDurationMin = stats.total_workouts > 0
    ? stats.total_duration_min / stats.total_workouts
    : null;

  const tiles: Array<[string, string, string]> = [
    ['Тренировочных дней',
      `${stats.days_with_workout} / ${windowDays(window)}`,
      ''],
    ['Средняя длительность',
      avgDurationMin != null ? formatNumber(avgDurationMin, 0) : '—',
      avgDurationMin != null ? 'мин' : ''],
    ['Средний пульс',
      stats.avg_hr != null ? formatNumber(stats.avg_hr, 0) : '—',
      stats.avg_hr != null ? 'уд/мин' : ''],
    ['Среднее восстановление',
      stats.avg_recovery != null ? formatNumber(stats.avg_recovery, 0) : '—',
      stats.avg_recovery != null ? '%' : ''],
    ['Энергия',
      totalKcal != null ? formatNumber(totalKcal, 0) : '—',
      totalKcal != null ? 'ккал' : ''],
  ];

  return h('section', { class: 'sleep-section' }, [
    h('h2', { class: 'sleep-section-header' }, [h('span', null, '⚡ Подробно')]),
    h('div', { class: 'deepdive-stat-grid' },
      tiles.map(([label, value, unit]) => DeepDiveTile(label, value, unit, DETAIL_TOOLTIPS[label]))),
  ]);
}

interface SportGroup {
  label: string;
  emoji: string;
  count: number;
  total_min: number;
  strain_sum: number;
  strain_n: number;
}

function groupBySportLabel(bySport: WhoopBySport[]): SportGroup[] {
  const groups = new Map<string, SportGroup>();
  for (const b of bySport) {
    const label = sportLabel(b.sport_name);
    const g = groups.get(label) ?? {
      label,
      emoji: sportEmoji(b.sport_name),
      count: 0,
      total_min: 0,
      strain_sum: 0,
      strain_n: 0,
    };
    g.count += b.count;
    g.total_min += b.total_min;
    if (b.avg_strain != null) {
      g.strain_sum += b.avg_strain * b.count;
      g.strain_n += b.count;
    }
    groups.set(label, g);
  }
  return Array.from(groups.values()).sort((a, b) => b.total_min - a.total_min);
}

function bySportSection(bySport: WhoopBySport[], window: WindowKey): HTMLElement {
  if (!bySport.length) {
    return h('section', { class: 'sleep-section' }, [
      h('h2', { class: 'sleep-section-header' }, [h('span', null, '🏃 По типу')]),
      h('p', { class: 'sleep-section-hint' }, `Нет тренировок за окно ${WINDOW_LABELS[window]}.`),
    ]);
  }

  const groups = groupBySportLabel(bySport);
  const maxMin = Math.max(1, ...groups.map((g) => g.total_min));
  const totalMin = groups.reduce((s, g) => s + g.total_min, 0);

  const rows = groups.map((g, i) => {
    const avgStrain = g.strain_n > 0 ? g.strain_sum / g.strain_n : null;
    const pctOfTotal = totalMin > 0 ? (g.total_min / totalMin) * 100 : 0;
    const pctOfMax = (g.total_min / maxMin) * 100;
    const color = SPORT_COLORS[i % SPORT_COLORS.length];

    return h('li', { class: 'sport-row' }, [
      h('span', { class: 'sport-row-emoji', 'aria-hidden': 'true' }, g.emoji),
      h('span', { class: 'sport-row-label' }, g.label),
      h('span', { class: 'sport-row-bar', 'aria-hidden': 'true' }, [
        h('span', {
          class: 'sport-row-bar-fill',
          style: `width:${pctOfMax.toFixed(2)}%; background:${color};`,
        }),
      ]),
      h('span', { class: 'sport-row-pct tabular' }, `${pctOfTotal.toFixed(0)}%`),
      h('span', { class: 'sport-row-meta tabular' },
        `${g.count} тр · ${Math.round(g.total_min)} мин · strain ${avgStrain != null ? formatNumber(avgStrain, 1) : '—'}`),
    ]);
  });

  return h('section', { class: 'sleep-section' }, [
    h('h2', { class: 'sleep-section-header' }, [h('span', null, '🏃 По типу')]),
    h('ul', { class: 'sport-list', role: 'list' }, rows),
  ]);
}

function recentWorkoutsSection(workouts: WhoopWorkout[]): HTMLElement {
  type Row = {
    when: string;
    sport: string;
    duration: string;
    strain: string;
    avg_hr: string;
    max_hr: string;
    _raw: WhoopWorkout;
  } & Record<string, unknown>;

  const rows: Row[] = workouts.map((w) => ({
    when: w.start ? formatDate(w.start.slice(0, 10)) : '—',
    sport: sportLabel(w.sport_name),
    duration: w.duration_min != null ? `${Math.round(w.duration_min)} мин` : '—',
    strain: w.strain != null ? formatNumber(w.strain, 1) : '—',
    avg_hr: w.avg_hr != null ? `${Math.round(w.avg_hr)}` : '—',
    max_hr: w.max_hr != null ? `${Math.round(w.max_hr)}` : '—',
    _raw: w,
  }));

  const table = Table<Row>({
    ariaLabel: `Последние ${workouts.length} тренировок`,
    empty: 'Нет тренировок за период.',
    columns: [
      { key: 'when', header: 'Дата' },
      { key: 'sport', header: 'Тип' },
      { key: 'duration', header: 'Длит.', align: 'right' },
      { key: 'strain', header: 'Strain', align: 'right' },
      { key: 'avg_hr', header: 'Ср. пульс', align: 'right' },
      { key: 'max_hr', header: 'Макс. пульс', align: 'right' },
    ],
    rows,
  });

  const tableWrap = h('div', { class: 'recent-workouts-table-wrap' }, table);

  const cards = workouts.map((w) => {
    const emoji = sportEmoji(w.sport_name);
    const label = sportLabel(w.sport_name);
    const date = w.start ? formatDate(w.start.slice(0, 10)) : '—';
    const dur = w.duration_min != null ? `${Math.round(w.duration_min)} мин` : '—';
    const strainVal = w.strain != null ? formatNumber(w.strain, 1) : '—';
    const avgHr = w.avg_hr != null ? `${Math.round(w.avg_hr)}` : '—';
    const maxHr = w.max_hr != null ? `${Math.round(w.max_hr)}` : '—';

    return h('li', { class: 'workout-card', role: 'listitem' }, [
      h('div', { class: 'wc-header' }, [
        h('div', { class: 'wc-sport' }, [
          ...(emoji ? [h('span', { class: 'wc-sport-emoji', 'aria-hidden': 'true' }, emoji)] : []),
          h('span', { class: 'wc-sport-label' }, label),
        ]),
        h('div', { class: 'wc-header-meta' }, [
          h('span', null, date),
          h('span', { 'aria-hidden': 'true' }, '·'),
          h('span', null, dur),
        ]),
      ]),
      h('div', { class: 'wc-metrics-grid' }, [
        h('div', { class: 'wc-metric-cell' }, [
          h('span', { class: 'wc-metric-label' }, 'Strain'),
          h('span', { class: 'wc-metric-value' }, strainVal),
        ]),
        h('div', { class: 'wc-metric-cell' }, [
          h('span', { class: 'wc-metric-label' }, 'Ср. пульс'),
          h('span', { class: 'wc-metric-value' }, avgHr),
          ...(w.avg_hr != null ? [h('span', { class: 'wc-metric-unit' }, 'уд/мин')] : []),
        ]),
        h('div', { class: 'wc-metric-cell' }, [
          h('span', { class: 'wc-metric-label' }, 'Макс. пульс'),
          h('span', { class: 'wc-metric-value' }, maxHr),
          ...(w.max_hr != null ? [h('span', { class: 'wc-metric-unit' }, 'уд/мин')] : []),
        ]),
      ]),
    ]);
  });

  const cardList = h('ol', { class: 'workout-card-list', role: 'list' }, cards);

  return h('section', { class: 'sleep-section' }, [
    h('h2', { class: 'sleep-section-header' }, [
      h('span', null, `📋 Последние тренировки · ${workouts.length}`),
    ]),
    tableWrap,
    cardList,
  ]);
}

function chartsSection(
  recovery: WhoopRecoveryPoint[],
  strain: WhoopStrainPoint[],
  window: WindowKey,
): HTMLElement {
  // parseDate, а не UTC-полночь: подписи осей читают эти значения локальными
  // геттерами (SingleLineChart), поэтому западнее Гринвича день съезжал назад.
  const specs: ChartSpec[] = [
    {
      title: 'Восстановление',
      unit: '%',
      xs: recovery.map((r) => (parseDate(r.day)?.getTime() ?? NaN) / 1000),
      values: recovery.map((r) => r.score ?? null),
      fractionDigits: 0,
    },
    {
      title: 'Strain',
      unit: '',
      xs: strain.map((s) => (parseDate(s.day)?.getTime() ?? NaN) / 1000),
      values: strain.map((s) => s.strain ?? null),
      fractionDigits: 1,
    },
  ];

  return sharedChartsSection(specs, window);
}

